// Drift: Dokumentationsarkivet, Klagecenterets afgørelser og AI-vurdering, og chatbotten (del af SupabaseApi – se supabase.ts).
// Databasen: 20261105000000_dokarkiv.sql, 20261105010000_klagecenter.sql og 20261105020000_chatbot.sql. Edge-funktionerne
// "klage-vurdering" og "chatbot". Billederne i arkivet får links, der udløber efter ARKIV_LINK_SEK – først når en mappe åbnes.
import { ApiFejl, type Api, type BotSamtaleDetalje, type BotSession, type BotSvar } from './api';
import { ARKIV_LINK_SEK, kortNavn, maanedNavnStort, arkivDag, type ArkivBillede, type ArkivBooking, type ArkivMappe } from '../../core/dokArkiv';
import { fletBotRegler, type BotRegler } from '../../core/chatbot';
import { tilVisning } from './sbKontor';
import type { SbKontekst } from './sbKatalog';

type R = Record<string, unknown>;
const LAGER = 'lager:';
const fejl = (b: string) => new ApiFejl(b);

/** Kan Supabase lave miniaturer (image transformation – kræver Pro)? Prøves én gang pr. session. */
let transform: Promise<boolean> | null = null;

export class SbKundeservice {
  constructor(private readonly c: SbKontekst) {}
  private async rpc<T>(navn: string, args: R = {}): Promise<T> {
    return (await this.c.k(this.c.sb.rpc(navn, { k: await this.c.kraevKlient(), ...args }))) as T;
  }

  // ---------- Dokumentationsarkivet ----------
  arkivMapper: Api['arkivMapper'] = async (s) => {
    const l = await this.rpc<{ noegle: string; steamer: string; bookinger: number; billeder: number; dage: number }[]>('dokarkiv_mapper', { p: s });
    return l.map((x): ArkivMappe => ({
      noegle: x.noegle, bookinger: Number(x.bookinger), billeder: Number(x.billeder),
      ...(s.niveau === 'aar' || s.niveau === 'maaned' ? { dage: Number(x.dage) } : {}),
      ...(s.niveau === 'steamer' ? { steamer_id: x.noegle === '-' ? null : x.noegle } : {}),
      navn: s.niveau === 'aar' ? x.noegle : s.niveau === 'maaned' ? maanedNavnStort(Number(x.noegle.slice(5, 7))) : s.niveau === 'dag' ? arkivDag(x.noegle) : x.noegle === '-' ? 'Ingen steamer' : kortNavn(x.steamer),
    })).sort((a, b) => (s.niveau === 'steamer' ? b.billeder - a.billeder : b.noegle.localeCompare(a.noegle)));
  };
  arkivAaben: Api['arkivAaben'] = async (dag, steamer_id, filter) => {
    const l = await this.rpc<{ booking: Parameters<typeof tilVisning>[0]; sag_id: number | null; dok: (Omit<ArkivBillede, 'url' | 'thumb'> & { billede: string })[] }[]>(
      'dokarkiv_mappe', { p_dag: dag, p_steamer: steamer_id, p_filter: filter });
    const stier = [...new Set(l.flatMap((x) => x.dok.map((d) => d.billede)).filter((b) => b.startsWith(LAGER)).map((b) => b.slice(LAGER.length)))];
    const links = new Map<string, string>();
    for (let i = 0; i < stier.length; i += 500) {
      const { data } = await this.c.sb.storage.from('steamer-billeder').createSignedUrls(stier.slice(i, i + 500), ARKIV_LINK_SEK);
      for (const x of data ?? []) if (x.path && x.signedUrl) links.set(LAGER + x.path, x.signedUrl);
    }
    const bookinger = l.map(({ booking, sag_id, dok }): ArkivBooking => {
      const b = tilVisning(booking);
      return {
        id: b.id, start: b.start, slut: b.slut, dato: b.dato, nummerplade: b.bil?.nummerplade ?? '', bil: b.bil?.maerke_model ?? '', ydelse: [b.ydelse.navn, ...b.tilvalgNavne.filter((t) => t.pris > 0).map((t) => t.navn)].join(' + '),
        kunde: b.kunde.navn, adresse: [b.adresse, [b.postnr, b.by].filter(Boolean).join(' ')].filter(Boolean).join(', '), steamer: b.steamer?.navn ?? '', sag_id,
        billeder: dok.map((d) => ({ id: d.id, type: d.type, vinkel: d.vinkel, taget: String(d.taget), note: d.note ?? null, url: links.get(d.billede) ?? (d.billede.startsWith(LAGER) ? '' : d.billede), thumb: null })).filter((d) => d.url),
      };
    });
    return { dag, steamer_id, steamer: bookinger[0]?.steamer ?? '', hentet: new Date().toISOString(), bookinger };
  };
  /** Miniaturer til én booking, hvis Supabase kan lave dem (image transformation). Ellers {} – så vises billedet selv (lazy). */
  arkivMiniaturer: Api['arkivMiniaturer'] = async (urls) => {
    const stier = urls.map((u) => /\/object\/sign\/steamer-billeder\/([^?]+)/.exec(u)?.[1]).filter((x): x is string => !!x).map(decodeURIComponent);
    if (!stier.length) return {};
    const lager = this.c.sb.storage.from('steamer-billeder');
    const mini = (sti: string) => lager.createSignedUrl(sti, ARKIV_LINK_SEK, { transform: { width: 240, height: 160, resize: 'cover', quality: 60 } });
    transform ??= (async () => {
      try {
        const { data } = await mini(stier[0]);
        return !!data?.signedUrl && (await fetch(data.signedUrl)).ok;
      } catch { return false; }
    })();
    if (!(await transform)) return {};
    const ud: Record<string, string> = {};
    await Promise.all(stier.map(async (sti, i) => { const { data } = await mini(sti); if (data?.signedUrl) ud[urls.find((u) => u.includes(encodeURI(sti)) || u.includes(sti)) ?? urls[i]] = data.signedUrl; }));
    return ud;
  };
  arkivSoeg: Api['arkivSoeg'] = async (q) => (q.trim().length < 2 && !/^#?\d+$/.test(q.trim()) ? [] : this.rpc('dokarkiv_soeg', { p_q: q.trim().slice(0, 80) }));

  // ---------- Klagecenter ----------
  klageAfgoer: Api['klageAfgoer'] = async (id, a) => {
    await this.rpc('gem_klage_afgoerelse', { p_id: id, p: a });
    this.c.skift();
  };
  opretKontorSag: Api['opretKontorSag'] = async (a) => { const id = await this.rpc<number>('opret_kontor_sag', { p: a }); this.c.skift(); return { id: Number(id) }; };
  klageSvar: Api['klageSvar'] = async (id, tekst) => { await this.rpc('gem_klage_svar', { p_id: id, p_tekst: tekst.slice(0, 6000) }); this.c.skift(); };
  klageAi: Api['klageAi'] = async (id) => {
    const r = await this.c.funktion<{ forbundet: boolean; vurdering: never; fejl?: string }>('klage-vurdering', { handling: 'vurder', k: await this.c.kraevKlient(), sag_id: id });
    if (r.fejl) throw fejl(r.fejl);
    this.c.skift();
    return { forbundet: r.forbundet, vurdering: r.vurdering ?? null };
  };
  aiStatus: Api['aiStatus'] = async () => {
    try { return await this.c.funktion<{ forbundet: boolean; fejl?: string }>('klage-vurdering', { handling: 'status' }); } catch { return { forbundet: false }; }
  };

  // ---------- Chatbot: kunden (widget'en) ----------
  private bot = <T,>(body: R) => this.c.funktion<T>('chatbot', body);
  botStart: Api['botStart'] = async (brand) => {
    const a = this.c.adresse();
    if (!a) throw fejl('Adressen findes ikke.');
    return this.bot({ handling: 'start', vaert: a.vaert, slug: a.slug, brand });
  };
  botSend: Api['botSend'] = (s, input) => this.bot<BotSvar>({ handling: 'send', id: s.id, token: s.token, input });
  botHent: Api['botHent'] = (s, efter) => this.bot<BotSvar>({ handling: 'hent', id: s.id, token: s.token, efter });
  botBedoem: Api['botBedoem'] = async (s, stjerner) => { await this.bot({ handling: 'bedoem', id: s.id, token: s.token, stjerner }); };
  botLyt = (s: BotSession, fn: () => void) => {
    const ch = this.c.sb.channel('bot-' + s.kanal).on('broadcast', { event: 'ny' }, () => fn()).subscribe();
    return () => { void this.c.sb.removeChannel(ch); };
  };

  // ---------- Chatbot: kontoret ----------
  botOverblik: Api['botOverblik'] = async () => this.rpc('bot_overblik');
  botSamtaler: Api['botSamtaler'] = async () => this.rpc('bot_samtaler_liste');
  botSamtale: Api['botSamtale'] = async (id) => {
    const x = await this.rpc<Omit<BotSamtaleDetalje, 'booking'> & { booking: Parameters<typeof tilVisning>[0] | null }>('bot_samtale', { p_id: id });
    return { ...x, booking: x.booking ? tilVisning(x.booking) : null };
  };
  botOvertag: Api['botOvertag'] = async (id) => { await this.rpc('bot_overtag', { p_id: id }); this.c.skift(); };
  botGivTilbage: Api['botGivTilbage'] = async (id) => { await this.rpc('bot_giv_tilbage', { p_id: id }); this.c.skift(); };
  botSvar: Api['botSvar'] = async (id, tekst) => { await this.rpc('bot_svar', { p_id: id, p_tekst: tekst.slice(0, 2000) }); this.c.skift(); };
  botLuk: Api['botLuk'] = async (id) => { await this.rpc('bot_luk', { p_id: id }); this.c.skift(); };
  botRegler: Api['botRegler'] = async () => {
    const k = await this.c.kraevKlient();
    const [r, kl, ai] = await Promise.all([
      this.c.k(this.c.sb.from('bot_opsaetning').select('regler').eq('klient_id', k).maybeSingle()) as Promise<{ regler: unknown } | null>,
      (this.c.k(this.c.sb.rpc('udsendelse_er_spaerret', { k })) as Promise<unknown>).catch(() => true),
      this.aiStatus(),
    ]);
    const firma = (await this.c.k(this.c.sb.from('klienter').select('navn').eq('id', k).maybeSingle()) as { navn: string } | null)?.navn ?? '';
    return { regler: fletBotRegler(r?.regler, firma), ai: ai.forbundet, ...(ai.fejl ? { aiFejl: ai.fejl } : {}), spaerret: kl !== false };
  };
  gemBotRegler: Api['gemBotRegler'] = async (r: BotRegler) => { await this.rpc('gem_bot_regler', { p: r }); this.c.skift(); };
  botProfilBillede: Api['botProfilBillede'] = async (data) => {
    const m = /^data:(image\/(jpeg|png|webp));base64,(.+)$/.exec(data);
    if (!m) throw new Error('Vælg et billede (JPG, PNG eller WebP).');
    const bytes = Uint8Array.from(atob(m[3]), (c) => c.charCodeAt(0));
    if (bytes.length > 300_000) throw new Error('Billedet er for stort (højst 300 KB).');
    const sti = `${await this.c.kraevKlient()}/${crypto.randomUUID()}.${m[2] === 'jpeg' ? 'jpg' : m[2]}`;
    await this.c.k(this.c.sb.storage.from('bot-profiler').upload(sti, bytes, { contentType: m[1], upsert: false }));
    return sti;
  };
  botBilledeUrl: Api['botBilledeUrl'] = (sti) => (sti.startsWith('data:') ? sti : this.c.sb.storage.from('bot-profiler').getPublicUrl(sti).data.publicUrl);
  botViden: Api['botViden'] = async () => {
    const k = await this.c.kraevKlient();
    const [v, konf, ops, kat] = await Promise.all([
      this.c.k(this.c.sb.from('bot_viden').select('id, type, url, titel, spoergsmaal, svar, opdateret').eq('klient_id', k).order('opdateret', { ascending: false })) as Promise<R[]>,
      this.c.k(this.c.sb.from('bot_konflikter').select('id, url, citat, freemi').eq('klient_id', k).eq('status', 'aaben').order('oprettet')) as Promise<{ id: string; url: string; citat: string; freemi: string }[]>,
      this.c.k(this.c.sb.from('bot_opsaetning').select('hentet, hentet_sider, hentet_status').eq('klient_id', k).maybeSingle()) as Promise<R | null>,
      this.c.k(this.c.sb.from('indstillinger').select('data').eq('klient_id', k).maybeSingle()) as Promise<{ data: { firma?: { hjemmeside?: string; handelsbetingelser?: string }; gebyrer?: Record<string, number> } } | null>,
    ]);
    const [ydelser, tilvalg, zoner] = await Promise.all([
      this.c.k(this.c.sb.from('ydelser').select('id', { count: 'exact', head: false }).eq('klient_id', k).eq('aktiv', true).eq('online', true)) as Promise<unknown[]>,
      this.c.k(this.c.sb.from('tilvalg').select('id').eq('klient_id', k).eq('aktiv', true)) as Promise<unknown[]>,
      this.c.k(this.c.sb.from('zoner').select('navn').eq('klient_id', k)) as Promise<{ navn: string }[]>,
    ]);
    const firma = kat?.data?.firma ?? {};
    const g = kat?.data?.gebyrer ?? {};
    const bet = v.filter((x) => x.type === 'betingelser');
    return {
      live: { ydelser: ydelser.length, tilvalg: tilvalg.length, zoner: zoner.length ? zoner.map((z) => z.navn).join(', ') : 'Hele Danmark', gebyrer: ['flyt', 'aflys24', 'aflys48', 'weekend', 'helligdag'].filter((x) => (g[x] ?? 1) > 0).length },
      hjemmeside: { url: firma.hjemmeside ?? '', sider: Number(ops?.hentet_sider ?? 0), hentet: (ops?.hentet as string) ?? null, status: (ops?.hentet_status as string) ?? null },
      betingelser: firma.handelsbetingelser ? { url: firma.handelsbetingelser, sider: bet.length, hentet: (bet[0]?.opdateret as string) ?? null } : null,
      faq: v.filter((x) => x.type === 'faq').map((x) => ({ id: String(x.id), spoergsmaal: String(x.spoergsmaal ?? ''), svar: String(x.svar ?? ''), opdateret: String(x.opdateret) })),
      konflikter: konf,
    };
  };
  gemBotFaq: Api['gemBotFaq'] = async (p) => { await this.rpc('gem_bot_faq', { p }); this.c.skift(); };
  botHentHjemmeside: Api['botHentHjemmeside'] = async () => {
    const r = await this.bot<{ sider: number; konflikter: number }>({ handling: 'hent_side', k: await this.c.kraevKlient() });
    this.c.skift();
    return r;
  };
  botIgnorerKonflikt: Api['botIgnorerKonflikt'] = async (id) => { await this.rpc('bot_konflikt_ignorer', { p_id: id }); this.c.skift(); };
  botTest: Api['botTest'] = async (spoergsmaal) => this.bot({ handling: 'test', k: await this.c.kraevKlient(), spoergsmaal: spoergsmaal.slice(0, 1000) });
}
